import { useEffect, useRef, useState } from "react";
import { WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

/** How often the notice re-probes the backend on its own. */
const RECONNECT_PROBE_MS = 15_000;

interface ConnectionLostNoticeProps {
  onRetry: () => Promise<void>;
}

/**
 * Non-blocking notice for a verified session that lost the backend mid-use.
 * The page underneath stays mounted (an editor keeps its in-memory edits);
 * the notice disappears on the next request that reaches the backend, and it
 * probes on its own so an idle page recovers too.
 */
export const ConnectionLostNotice = ({ onRetry }: ConnectionLostNoticeProps) => {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  useEffect(() => {
    const id = window.setInterval(() => {
      void onRetryRef.current();
    }, RECONNECT_PROBE_MS);
    return () => window.clearInterval(id);
  }, []);

  const retry = async () => {
    setChecking(true);
    try {
      await onRetry();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[100] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-card-foreground shadow-lg"
    >
      <WifiOff className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t('auth.connectionLost.title')}</p>
        <p className="text-xs text-muted-foreground">{t('auth.connectionLost.description')}</p>
      </div>
      <Button type="button" size="sm" variant="outline" disabled={checking} onClick={() => { void retry(); }}>
        {t('common.retry')}
      </Button>
    </div>
  );
};
